import {
  CreateAgendaSlotBulkApiResponseError,
  getDayOfWeekOf,
  HttpErrors,
  PeriodOfTheDay,
  workScheduleBetweenItems,
} from '@omecare/utils';
import { format } from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AddOfficeWorkScheduleSlotFormValues } from '@/components/forms/add-office-work-schedule-slot-form/schema';
import { useAddOfficeWorkScheduleSlotForm } from '@/components/forms/add-office-work-schedule-slot-form/use-add-office-work-schedule-slot-form';
import { pushModal } from '@/components/modals';
import useAddOfficeWorkScheduleSlotMutation from '@/hooks/mutations/use-add-office-work-schedule-slot';
import useAddOfficeWorkScheduleSlotBulkMutation from '@/hooks/mutations/use-add-office-work-schedule-slot-bulk';
import { OfficeDoctorAttendingItem } from '@/hooks/queries/use-get-office';
import { SheetViewModel } from '../sheet.viewmodel';

export const selectAddOfficeWorkScheduleSlotSheetViewModel = ({
  sheetViewModel,
  officeId,
  doctorAttendings,
}: {
  sheetViewModel: SheetViewModel;
  officeId: string;
  doctorAttendings: OfficeDoctorAttendingItem[];
}) => {
  const { t } = useTranslation();

  const { addOfficeWorkScheduleSlotForm } = useAddOfficeWorkScheduleSlotForm({
    doctorAttendingId:
      doctorAttendings.length > 0 ? doctorAttendings[0]!.id : undefined,
  });

  const addOfficeWorkScheduleSlotMutation =
    useAddOfficeWorkScheduleSlotMutation();

  const addOfficeWorkScheduleSlotBulkMutation =
    useAddOfficeWorkScheduleSlotBulkMutation();

  const submitMutation = async (
    values: AddOfficeWorkScheduleSlotFormValues,
  ) => {
    // Transform allday selections into morning + afternoon slots
    const slots = values.specificDates.flatMap((date) => {
      const dayOfWeek = getDayOfWeekOf(date);
      const formattedDate = format(date, 'yyyy-MM-dd');
      const tzOffset = date.getTimezoneOffset();

      if (values.periodOfTheDay === 'allday') {
        // Create two slots: morning + afternoon
        const morningPeriod = workScheduleBetweenItems.find(
          (item) => item.id === 'morning',
        )!;
        const afternoonPeriod = workScheduleBetweenItems.find(
          (item) => item.id === 'afternoon',
        )!;

        return [
          {
            dayOfWeek,
            periodOfTheDay: 'morning' as PeriodOfTheDay,
            startAt: {
              date: formattedDate,
              tzOffset,
              between: morningPeriod.value!,
            },
          },
          {
            dayOfWeek,
            periodOfTheDay: 'afternoon' as PeriodOfTheDay,
            startAt: {
              date: formattedDate,
              tzOffset,
              between: afternoonPeriod.value!,
            },
          },
        ];
      } else {
        // Single slot for morning or afternoon
        const workPeriod = workScheduleBetweenItems.find(
          (workSchedule) => workSchedule.id === values.periodOfTheDay,
        )!;

        return [
          {
            dayOfWeek,
            periodOfTheDay: workPeriod.id as PeriodOfTheDay,
            startAt: {
              date: formattedDate,
              tzOffset,
              between: workPeriod.value!,
            },
          },
        ];
      }
    });

    addOfficeWorkScheduleSlotBulkMutation.mutate(
      {
        officeId,
        doctorAttendingId: values.doctorAttendingId,
        type: values.slotType,
        forceWithoutAssistant: values.forceWithoutAssistant,
        slots,
      },
      {
        onSuccess(data) {
          sheetViewModel.api.close({
            success: true,
            data,
          });
        },
        onError: (error: HttpErrors) => {
          if ('data' in error) {
            sheetViewModel.api.hide();
            pushModal('CreateAgendaSlotBulkReportModal', {
              report: {
                error: error.data as CreateAgendaSlotBulkApiResponseError,
              },
              officeId,
              onClose: () => {
                sheetViewModel.api.show();
              },
            });
          }
        },
      },
    );
  };

  const submit = () => {
    addOfficeWorkScheduleSlotForm.handleSubmit(submitMutation, () => {
      // Surface validation failures that would otherwise be swallowed — the
      // doctor-attending selector is hidden for attending doctors, so a
      // missing/invalid `doctorAttendingId` has no FormMessage to render into.
      toast.error(t('add-office-work-schedule-slot-sheet.validation-error'));
    })();
  };

  return {
    form: addOfficeWorkScheduleSlotForm,
    data: {
      loading:
        addOfficeWorkScheduleSlotMutation.isPending ||
        addOfficeWorkScheduleSlotBulkMutation.isPending,
    },
    api: {
      submit,
    },
  };
};
